import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './components/ui/tabs';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import OrgChartFlow from './components/OrgChartFlow';
import OrganigrammeList from './components/OrganigrammeList';
import { DossiersListPage, DossierFormPage, DossierDetailPage } from './components/Dossiers';
import VisitorAccessPage from './components/VisitorAccessPage';
import AdvisorsPage from './components/AdvisorsPage';
import DashboardPremium from './components/DashboardPremium';
import { InstallPWABanner } from './components/InstallPWABanner';

// Shared components - imported from extracted modules
import { AuthProvider, ProtectedRoute, useAuth, useApi } from './contexts/AuthContext';
import { DynamicTranslationProvider, useDynamicT } from './contexts/DynamicTranslationContext';
import { useTranslation } from 'react-i18next';
import { Layout } from './components/Layout';
import { PhotoDisplay, PhotoUploader, Lightbox, ClickablePhoto } from './components/media';
import { VideoPlayer, VideoUploader, VideoLightbox, ClickableVideo, InlineVideoPlayer } from './components/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline dynamic-translation wrapper for a single string child. Falls back to
// the original text while the async /api/translate call resolves.
const DTInline = ({ children }) => {
  const text = typeof children === 'string' ? children : null;
  const translated = useDynamicT(text);
  if (typeof children !== 'string') return children;
  return translated;
};

// Dashboard Page
const DashboardPage = () => {
  const api = useApi();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [valuationHistory, setValuationHistory] = useState([]);
  const [rentHistory, setRentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, valuationRes, rentRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/valuation-history"),
          api.get("/dashboard/rent-history")
        ]);
        setStats(statsRes.data);
        setValuationHistory(valuationRes.data);
        setRentHistory(rentRes.data);
      } catch (e) {
        toast.error("Erreur lors du chargement des données");
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-40 bg-muted" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="animate-fade-in">
          <p className="label-uppercase mb-2">Tableau de bord</p>
          <h1 className="font-heading text-4xl text-foreground">Portefeuille Immobilier</h1>
        </div>
        
        {/* Valorisation Totale - full width */}
        <div className="bg-white border border-border p-6 animate-fade-in" data-testid="metric-valuation">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="label-uppercase mb-1">Valorisation Totale</p>
              <p className="font-heading text-4xl text-foreground">{formatCurrency(stats?.total_valuation || 0)}</p>
              <p className="text-sm text-muted-foreground mt-1">{stats?.patrimonial_assets || 0} actifs patrimoniaux · LTV moy: {stats?.average_ltv?.toFixed(1) || 0}%</p>
            </div>
          </div>
          {valuationHistory && valuationHistory.length > 0 && (
            <div className="h-48 -mx-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={valuationHistory.map(d => ({
                  month: d.month,
                  total: (d.habitation || 0) + (d.hotelerie || 0) + (d.commercial || 0) + (d.promotion || 0) + (d.bureaux || 0) + (d.plage_restaurant || 0)
                }))} margin={{ top: 5, right: 10, left: 10, bottom: 15 }}>
                  <defs>
                    <linearGradient id="gradValuation" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#C5A059" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#999' }} tickFormatter={(m) => { const [y,mo] = m.split('-'); return `${mo}/${y.slice(2)}`; }} interval={Math.max(0, Math.floor((valuationHistory?.length || 1) / 8) - 1)} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Valorisation']}
                    contentStyle={{ fontSize: '12px', border: '1px solid #E5E2DE' }}
                  />
                  <Area type="monotone" dataKey="total" stroke="#C5A059" strokeWidth={2} fill="url(#gradValuation)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        
        {/* 3 Rent categories on same row with yields below */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Loyers Commerciaux & Bureaux */}
          <div className="bg-white border border-border p-6 animate-fade-in" data-testid="metric-rent-commercial">
            <p className="label-uppercase mb-1">Loyers Commerciaux & Bureaux</p>
            <p className="font-heading text-3xl text-foreground mb-1">{formatCurrency(stats?.total_annual_rent || 0)}</p>
            <p className="text-sm text-muted-foreground mb-4">Commercial, Bureaux, Plage</p>
            {rentHistory && rentHistory.length > 0 && (
              <div className="h-32 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={rentHistory.map(d => ({
                    month: d.month,
                    total: (d.commercial || 0) + (d.bureaux || 0) + (d.plage_restaurant || 0)
                  }))} margin={{ top: 5, right: 5, left: 5, bottom: 15 }}>
                    <defs>
                      <linearGradient id="gradRent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4A453F" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#4A453F" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#999' }} tickFormatter={(m) => { const [y,mo] = m.split('-'); return `${mo}/${y.slice(2)}`; }} interval={Math.max(0, Math.floor((rentHistory?.length || 1) / 4) - 1)} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Loyers']}
                      contentStyle={{ fontSize: '12px', border: '1px solid #E5E2DE' }}
                    />
                    <Area type="monotone" dataKey="total" stroke="#4A453F" strokeWidth={2} fill="url(#gradRent)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rendement</p>
              <p className="font-heading text-xl text-gold-dark">{stats?.average_yield_annual?.toFixed(2) || 0}%</p>
            </div>
          </div>
          
          {/* Loyers Hôtels */}
          <div className="bg-white border border-border p-6 animate-fade-in" data-testid="metric-rent-hotel">
            <p className="label-uppercase mb-1">Loyers Hôtels</p>
            <p className="font-heading text-3xl text-foreground mb-1">{formatCurrency(stats?.total_hotel_rent || 0)}</p>
            <p className="text-sm text-muted-foreground mb-4">Hôtellerie</p>
            {rentHistory && rentHistory.length > 0 && (
              <div className="h-32 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={rentHistory.map(d => ({
                    month: d.month,
                    total: (d.hotelerie || 0)
                  }))} margin={{ top: 5, right: 5, left: 5, bottom: 15 }}>
                    <defs>
                      <linearGradient id="gradHotel" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B7355" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8B7355" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#999' }} tickFormatter={(m) => { const [y,mo] = m.split('-'); return `${mo}/${y.slice(2)}`; }} interval={Math.max(0, Math.floor((rentHistory?.length || 1) / 4) - 1)} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Loyers Hôtels']}
                      contentStyle={{ fontSize: '12px', border: '1px solid #E5E2DE' }}
                    />
                    <Area type="monotone" dataKey="total" stroke="#8B7355" strokeWidth={2} fill="url(#gradHotel)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rendement</p>
              <p className="font-heading text-xl text-gold-dark">{stats?.average_yield_hotel?.toFixed(2) || 0}%</p>
            </div>
          </div>
          
          {/* Loyers Habitation & Promotion */}
          <div className="bg-white border border-border p-6 animate-fade-in" data-testid="metric-rent-seasonal">
            <p className="label-uppercase mb-1">Loyers Habitation & Promotion</p>
            <p className="font-heading text-3xl text-foreground mb-1">{formatCurrency(stats?.total_seasonal_revenue || 0)}</p>
            <p className="text-sm text-muted-foreground mb-4">Habitation & Promotion</p>
            {rentHistory && rentHistory.length > 0 && (
              <div className="h-32 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={rentHistory.map(d => ({
                    month: d.month,
                    total: (d.habitation || 0) + (d.promotion || 0)
                  }))} margin={{ top: 5, right: 5, left: 5, bottom: 15 }}>
                    <defs>
                      <linearGradient id="gradSeasonal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6B8E5A" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6B8E5A" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#999' }} tickFormatter={(m) => { const [y,mo] = m.split('-'); return `${mo}/${y.slice(2)}`; }} interval={Math.max(0, Math.floor((rentHistory?.length || 1) / 4) - 1)} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Rev. Saisonniers']}
                      contentStyle={{ fontSize: '12px', border: '1px solid #E5E2DE' }}
                    />
                    <Area type="monotone" dataKey="total" stroke="#6B8E5A" strokeWidth={2} fill="url(#gradSeasonal)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rendement</p>
              <p className="font-heading text-xl text-gold-dark">{stats?.average_yield_seasonal?.toFixed(2) || 0}%</p>
            </div>
          </div>
        </div>
        
        {/* Poles breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {['habitation', 'hotelerie', 'commercial', 'promotion', 'bureaux', 'plage_restaurant'].map((pole, idx) => (
            <PoleCard 
              key={pole}
              pole={pole}
              data={stats?.by_pole?.[pole]}
              formatCurrency={formatCurrency}
              delay={idx + 1}
            />
          ))}
        </div>
        
        {/* Sold Assets Section */}
        {stats?.sold_by_pole?.length > 0 && (() => {
          const totalAcquisition = stats.sold_by_pole.reduce((s, p) => s + p.total_acquisition_cost, 0);
          const totalSale = stats.sold_by_pole.reduce((s, p) => s + p.total_sale_price, 0);
          const totalMargin = totalSale - totalAcquisition;
          const totalCount = stats.sold_by_pole.reduce((s, p) => s + p.count, 0);
          const marginPct = totalAcquisition > 0 ? (totalMargin / totalAcquisition * 100) : 0;
          return (
          <div className="space-y-4">
            <h2 className="font-heading text-2xl">Actifs Cédés - Performance</h2>
            
            {/* Total summary */}
            <div className="bg-white border border-border p-6" data-testid="sold-total">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div>
                  <p className="label-uppercase mb-1">Actifs vendus</p>
                  <p className="font-heading text-2xl">{totalCount}</p>
                </div>
                <div>
                  <p className="label-uppercase mb-1">Coût de revient</p>
                  <p className="font-heading text-2xl tabular-nums">{formatCurrency(totalAcquisition)}</p>
                </div>
                <div>
                  <p className="label-uppercase mb-1">Prix de vente total</p>
                  <p className="font-heading text-2xl tabular-nums">{formatCurrency(totalSale)}</p>
                </div>
                <div className="col-span-2 md:col-span-2 border-l border-border pl-6">
                  <p className="label-uppercase mb-1">Marge Brute</p>
                  <p className={`font-heading text-3xl tabular-nums ${totalMargin >= 0 ? 'text-gold-dark' : 'text-destructive'}`}>
                    {totalMargin >= 0 ? '+' : ''}{formatCurrency(totalMargin)}
                  </p>
                  <p className={`text-sm font-medium ${totalMargin >= 0 ? 'text-gold-dark' : 'text-destructive'}`}>
                    {marginPct >= 0 ? '+' : ''}{marginPct.toFixed(1)}% de marge
                  </p>
                </div>
              </div>
            </div>
            
            {/* Detail by pole */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {stats.sold_by_pole.map((sold, idx) => (
                <SoldPoleCard 
                  key={sold.pole}
                  data={sold}
                  formatCurrency={formatCurrency}
                  delay={idx + 1}
                />
              ))}
            </div>
          </div>
          );
        })()}
        
        {/* Charts */}
        <div className="grid grid-cols-1 gap-6">
          <LineChartCard 
            title="Évolution des loyers / revenus"
            data={rentHistory}
            type="rent"
            formatValue={formatCurrency}
          />
        </div>
        
        {/* Quick actions */}
        {isAdmin && (
          <div className="flex gap-4">
            <Link
              to="/assets/new"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors"
              data-testid="add-asset-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Ajouter un actif
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
};

const MetricCard = ({ label, value, subtitle, accent, delay, testId }) => (
  <div 
    className={`bg-white border border-border p-8 animate-fade-in-delay-${delay} ${accent ? 'border-l-4 border-l-gold' : ''}`}
    data-testid={testId}
  >
    <p className="label-uppercase mb-4">{label}</p>
    <p className={`font-heading text-3xl mb-2 ${accent ? 'text-gold-dark' : 'text-foreground'} tabular-nums`}>
      {value}
    </p>
    <p className="text-sm text-muted-foreground">{subtitle}</p>
  </div>
);

const PoleCard = ({ pole, data, formatCurrency, delay }) => {
  const labels = {
    habitation: 'Habitation',
    hotelerie: 'Hôtellerie',
    commercial: 'Commercial',
    promotion: 'Promotion',
    bureaux: 'Bureaux',
    plage_restaurant: 'Autres',
    fonds_commerce: 'Fonds de commerce'
  };
  
  const icons = {
    habitation: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>,
    hotelerie: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16"/><path d="M9 7h6M9 11h6M9 15h6"/></svg>,
    commercial: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
    promotion: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M2 20h20M6 20V10l4-6h4l4 6v10M10 20v-6h4v6M10 10h4"/></svg>,
    bureaux: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 6h2M13 6h2M9 10h2M13 10h2M9 14h2M13 14h2M9 18h6"/></svg>,
    plage_restaurant: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M17 21H7M12 3v18M5 8c2-3 5-5 7-5s5 2 7 5M3 12c3-2 6-3 9-3s6 1 9 3"/></svg>,
    fonds_commerce: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M3 3h18v4H3zM5 7v12a2 2 0 002 2h10a2 2 0 002-2V7"/><path d="M10 12h4"/></svg>
  };
  
  const isHabitation = pole === 'habitation' || pole === 'promotion';
  const isFonds = pole === 'fonds_commerce';
  
  return (
    <Link 
      to={`/assets?pole=${pole}`}
      className={`bg-white border border-border p-6 hover:border-gold/50 transition-colors animate-fade-in-delay-${delay}`}
      data-testid={`pole-card-${pole}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-secondary flex items-center justify-center text-foreground">
          {icons[pole]}
        </div>
        <div>
          <p className="font-heading text-lg text-foreground">{labels[pole]}</p>
          <p className="text-sm text-muted-foreground">{data?.count || 0} actifs</p>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Valorisation</span>
          <span className="font-medium tabular-nums">{formatCurrency(data?.valuation || 0)}</span>
        </div>
        {!isFonds && (isHabitation ? (
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Revenus saisonniers</span>
            <span className="font-medium tabular-nums">{formatCurrency(data?.seasonal_revenue || 0)}/an</span>
          </div>
        ) : (
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Loyers annuels</span>
            <span className="font-medium tabular-nums">{formatCurrency(data?.annual_rent || 0)}/an</span>
          </div>
        ))}
        {!isFonds && (
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Rendement</span>
            <span className="font-medium text-gold-dark tabular-nums">{data?.yield?.toFixed(2) || 0}%</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">LTV moyen</span>
          <span className="font-medium tabular-nums">{data?.ltv?.toFixed(1) || 0}%</span>
        </div>
      </div>
    </Link>
  );
};

const SoldPoleCard = ({ data, formatCurrency, delay }) => (
  <div className={`bg-white border border-border p-6 animate-fade-in-delay-${delay}`} data-testid={`sold-pole-${data.pole}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading text-lg">{data.pole_label}</h3>
      <span className="text-xs uppercase tracking-wider bg-muted px-2 py-1">{data.count} vendu{data.count > 1 ? 's' : ''}</span>
    </div>
    <div className="space-y-3">
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Coût de revient</span>
        <span className="font-medium tabular-nums">{formatCurrency(data.total_acquisition_cost)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Prix de vente</span>
        <span className="font-medium tabular-nums">{formatCurrency(data.total_sale_price)}</span>
      </div>
      <div className="flex justify-between pt-2 border-t border-border">
        <span className="text-sm font-medium">Marge</span>
        <span className={`font-medium tabular-nums ${data.total_margin >= 0 ? 'text-gold-dark' : 'text-destructive'}`}>
          {data.total_margin >= 0 ? '+' : ''}{formatCurrency(data.total_margin)} ({data.margin_percentage}%)
        </span>
      </div>
    </div>
  </div>
);

const LineChartCard = ({ title, data, formatValue, type = "valuation" }) => {
  const poleColors = {
    habitation: "#C5A059",
    hotelerie: "#4A453F",
    commercial: "#8C8680",
    promotion: "#6B8E5A",
    bureaux: "#5A7B9A",
    plage_restaurant: "#D4825A",
    fonds_commerce: "#8B6F47"
  };
  
  const poleLabels = {
    habitation: "Habitation",
    hotelerie: "Hôtellerie",
    commercial: "Commercial",
    promotion: "Promotion",
    bureaux: "Bureaux",
    plage_restaurant: "Autres",
    fonds_commerce: "Fonds de commerce"
  };
  
  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value;
  };
  
  // Format X axis to show only years
  const formatXAxis = (month) => {
    if (!month) return '';
    return month.substring(0, 4); // Extract year from YYYY-MM
  };
  
  // Filter data to show only January of each year for cleaner display
  const getYearTicks = () => {
    if (!data || data.length === 0) return [];
    const years = new Set();
    return data
      .filter(d => {
        const year = d.month?.substring(0, 4);
        if (!years.has(year)) {
          years.add(year);
          return true;
        }
        return false;
      })
      .map(d => d.month);
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-border p-3 shadow-lg">
          <p className="font-heading text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {poleLabels[entry.dataKey] || entry.dataKey}: {formatValue(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="bg-white border border-border p-6" data-testid={`chart-${type}`}>
      <p className="label-uppercase mb-6">{title}</p>
      
      {!data || data.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          Aucune donnée disponible
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DE" />
              <XAxis 
                dataKey="month" 
                tickFormatter={formatXAxis}
                ticks={getYearTicks()}
                tick={{ fontSize: 11, fill: '#8C8680' }}
                tickLine={{ stroke: '#E5E2DE' }}
                axisLine={{ stroke: '#E5E2DE' }}
              />
              <YAxis 
                tickFormatter={formatYAxis}
                tick={{ fontSize: 11, fill: '#8C8680' }}
                tickLine={{ stroke: '#E5E2DE' }}
                axisLine={{ stroke: '#E5E2DE' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                formatter={(value) => poleLabels[value] || value}
                wrapperStyle={{ fontSize: '12px' }}
              />
              <Line 
                type="monotone" 
                dataKey="habitation" 
                stroke={poleColors.habitation} 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="hotelerie" 
                stroke={poleColors.hotelerie} 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="commercial" 
                stroke={poleColors.commercial} 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="promotion" 
                stroke={poleColors.promotion} 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="bureaux" 
                stroke={poleColors.bureaux} 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="plage_restaurant" 
                stroke={poleColors.plage_restaurant} 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Assets List Page
const AssetsListPage = () => {
  const api = useApi();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Initialize filter from URL
  const getInitialFilter = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      pole: searchParams.get('pole') || '',
      status: searchParams.get('status') || ''
    };
  };
  
  const [filter, setFilter] = useState(getInitialFilter);

  // Fetch assets when filter changes
  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter.pole) params.append('pole', filter.pole);
        if (filter.status) params.append('status', filter.status);
        
        const response = await api.get(`/assets?${params.toString()}`);
        setAssets(response.data);
      } catch (e) {
        toast.error(t('assets_list.load_error'));
      }
      setLoading(false);
    };
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  // Drag & drop reordering (admin only)
  const handleDragStart = (idx) => (e) => {
    if (!isAdmin) return;
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
  };
  const handleDragOver = (idx) => (e) => {
    if (!isAdmin || dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIndex) setDragOverIndex(idx);
  };
  const handleDragLeave = () => { setDragOverIndex(null); };
  const handleDrop = (idx) => async (e) => {
    if (!isAdmin || dragIndex === null) return;
    e.preventDefault();
    setDragOverIndex(null);
    if (idx === dragIndex) { setDragIndex(null); return; }
    const reordered = [...assets];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(idx, 0, moved);
    setAssets(reordered);
    setDragIndex(null);
    try {
      const payload = reordered.map((a, i) => ({ id: a.id, sort_order: i }));
      await api.put('/assets/reorder', payload);
      toast.success(t('assets_list.order_saved'));
    } catch (err) {
      toast.error(t('assets_list.order_error'));
    }
  };
  const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

  return (
    <Layout>
      <div className="space-y-8" data-testid="assets-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="animate-fade-in">
            <p className="label-uppercase mb-2">{t('assets_list.portfolio')}</p>
            <h1 className="font-heading text-4xl text-foreground">
              {filter.pole ? t(`poles.${filter.pole}`, { defaultValue: filter.pole }) : t('assets_list.all_assets')}
            </h1>
          </div>
          {isAdmin && (
            <Link
              to="/assets/new"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors"
              data-testid="new-asset-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t('assets_list.new_asset')}
            </Link>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <select
            value={filter.pole}
            onChange={(e) => setFilter(prev => ({ ...prev, pole: e.target.value }))}
            className="bg-white border border-border px-4 py-2 text-sm focus:outline-none focus:border-primary"
            data-testid="filter-pole"
          >
            <option value="">{t('assets_list.all_poles')}</option>
            <option value="habitation">{t('poles.habitation')}</option>
            <option value="hotelerie">{t('poles.hotelerie')}</option>
            <option value="commercial">{t('poles.commercial')}</option>
            <option value="promotion">{t('poles.promotion')}</option>
            <option value="bureaux">{t('poles.bureaux')}</option>
            <option value="plage_restaurant">{t('poles.plage_restaurant')}</option>
            <option value="fonds_commerce">{t('poles.fonds_commerce')}</option>
          </select>
          
          <select
            value={filter.status}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
            className="bg-white border border-border px-4 py-2 text-sm focus:outline-none focus:border-primary"
            data-testid="filter-status"
          >
            <option value="">{t('assets_list.all_status')}</option>
            <option value="patrimonial">{t('status.patrimonial')}</option>
            <option value="vendu">{t('status.vendu')}</option>
            <option value="marchand_de_bien">{t('status.marchand_de_bien')}</option>
          </select>
        </div>

        {isAdmin && assets.length > 1 && (
          <p className="text-xs text-muted-foreground italic" data-testid="reorder-hint">
            <svg className="w-3.5 h-3.5 inline mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M5 9l4-4 4 4M5 15l4 4 4-4M15 5l4 4-4 4M15 19l-4-4 4-4"/></svg>
            {t('assets_list.reorder_hint')}
          </p>
        )}
        
        {/* Cards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-48 bg-muted mb-4" />
                <div className="h-6 bg-muted w-3/4 mb-2" />
                <div className="h-4 bg-muted w-1/2" />
              </div>
            ))}
          </div>
        ) : assets.length === 0 ? (
          <div className="bg-white border border-border p-12 text-center">
            <p className="text-muted-foreground mb-4">{t('assets_list.empty')}</p>
            {isAdmin && (
              <Link
                to="/assets/new"
                className="text-foreground hover:text-gold-dark underline"
              >
                {t('assets_list.add_first')}
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="assets-grid">
            {assets.map((asset, idx) => {
              const isHabitation = asset.pole === 'habitation' || asset.pole === 'promotion';
              const isFonds = asset.pole === 'fonds_commerce';
              // Pour habitation/promotion, toujours afficher seasonal_revenue, sinon annual_rent
              const annualRevenue = isFonds ? 0 : (isHabitation ? (asset.seasonal_revenue || 0) : (asset.annual_rent || 0));
              const yieldRate = (!isFonds && asset.acquisition_price > 0) ? (annualRevenue / asset.acquisition_price) * 100 : 0;
              
              return (
                <div 
                  key={asset.id}
                  draggable={isAdmin}
                  onDragStart={handleDragStart(idx)}
                  onDragOver={handleDragOver(idx)}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                  data-testid={`asset-card-${asset.id}`}
                  className={`bg-white border transition-all flex flex-col ${
                    dragOverIndex === idx && dragIndex !== idx
                      ? 'border-gold border-2 shadow-xl scale-[1.02]'
                      : 'border-border hover:border-gold/50 hover:shadow-lg'
                  } ${dragIndex === idx ? 'opacity-40' : ''} ${isAdmin ? 'cursor-move' : ''}`}
                >
                  {/* Photo */}
                  <div className="relative aspect-[4/3] overflow-hidden flex-shrink-0">
                    {asset.main_photo ? (
                      <ClickablePhoto 
                        url={asset.main_photo} 
                        alt={asset.name} 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        allPhotos={[asset.main_photo, ...(asset.photos_before_works || []), ...(asset.photos_after_works || [])]}
                        photoIndex={0}
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <svg className="w-16 h-16 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
                          <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16"/>
                          <path d="M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5"/>
                        </svg>
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                        asset.status === 'patrimonial' 
                          ? 'bg-white/90 text-foreground' 
                          : asset.status === 'marchand_de_bien'
                          ? 'bg-gold/90 text-white'
                          : 'bg-foreground/80 text-white'
                      }`}>
                        {t(`status.${asset.status}`, { defaultValue: asset.status })}
                      </span>
                    </div>
                    
                    {/* Pole Badge */}
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 text-xs uppercase tracking-wider bg-gold/90 text-white">
                        {t(`poles.${asset.pole}`, { defaultValue: asset.pole })}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading text-xl mb-1 text-foreground"><DTInline>{asset.name}</DTInline></h3>
                    <p className="text-sm text-muted-foreground mb-4 truncate"><DTInline>{asset.address}</DTInline></p>
                    
                    {/* Stats */}
                    <div className={`grid ${isFonds ? 'grid-cols-1' : 'grid-cols-2'} gap-4 mb-4`}>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('asset.valuation')}</p>
                        <p className="font-heading text-lg tabular-nums">{formatCurrency(asset.current_valuation)}</p>
                      </div>
                      {!isFonds && (
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            {isHabitation ? t('asset.seasonal_revenue_short') : t('asset.annual_rent_short')}
                          </p>
                          <p className="font-heading text-lg tabular-nums">{formatCurrency(annualRevenue)}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-border mt-auto">
                      <div className="flex gap-4 text-sm">
                        {!isFonds && <span className="text-muted-foreground">{t('assets_list.yield_short')}: <span className="text-gold-dark font-medium">{yieldRate.toFixed(1)}%</span></span>}
                        <span className="text-muted-foreground">{t('asset.ltv')}: <span className="text-foreground font-medium">{asset.ltv_ratio?.toFixed(1) || 0}%</span></span>
                        <span className="text-muted-foreground">{t('assets_list.ownership_short')}: <span className="text-foreground font-medium">{asset.ownership_percentage?.toFixed(0) || 100}%</span></span>
                      </div>
                      <Link
                        to={`/assets/${asset.id}`}
                        className="text-gold-dark hover:text-gold transition-colors font-medium text-sm uppercase tracking-wider"
                        data-testid={`view-asset-${asset.id}`}
                      >
                        {t('assets_list.view')} →
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

// Asset Detail Page
const AssetDetailPage = () => {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const [asset, setAsset] = useState(null);
  const [ownerCompany, setOwnerCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activePhotoTab, setActivePhotoTab] = useState('main');

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        const response = await api.get(`/assets/${id}`);
        setAsset(response.data);
        if (response.data?.company_id) {
          try {
            const nodes = await api.get('/orgchart');
            const company = nodes.data.find(n => n.id === response.data.company_id);
            if (company) setOwnerCompany(company);
          } catch (_) { /* ignore */ }
        } else {
          setOwnerCompany(null);
        }
      } catch (e) {
        toast.error(t('asset.not_found'));
        navigate("/assets");
      }
      setLoading(false);
    };
    fetchAsset();
  }, [id]);

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/${id}`);
      toast.success(t('asset.deleted'));
      navigate("/assets");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('asset.delete_error'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
    return new Date(dateStr).toLocaleDateString(locale);
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-64 bg-muted" />
          <div className="h-8 bg-muted w-64" />
          <div className="h-48 bg-muted" />
        </div>
      </Layout>
    );
  }

  if (!asset) return null;

  const isHabitation = asset.pole === 'habitation' || asset.pole === 'promotion';
  const isSold = asset.status === 'vendu';
  const isFondsCommerce = asset.pole === 'fonds_commerce';
  
  const annualRent = isFondsCommerce ? 0 : (isHabitation 
    ? (asset.seasonal_revenue || 0)
    : (asset.annual_rent || 0));
  const yieldRate = (!isFondsCommerce && asset.acquisition_price > 0) ? (annualRent / asset.acquisition_price) * 100 : 0;
  const valueChange = isSold 
    ? (asset.sale_price || 0) - asset.acquisition_price
    : asset.current_valuation - asset.acquisition_price;
  const valueChangePercent = asset.acquisition_price > 0 ? (valueChange / asset.acquisition_price) * 100 : 0;

  return (
    <Layout>
      <div className="space-y-8" data-testid="asset-detail-page">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          {t('asset.back')}
        </button>
        
        {/* Header with photo */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Photo section */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border p-4">
              {asset.main_photo ? (
                <ClickablePhoto 
                  url={asset.main_photo} 
                  alt={asset.name} 
                  className="w-full aspect-[4/3] object-cover mb-4 cursor-pointer"
                  allPhotos={[asset.main_photo, ...(asset.photos_before_works || []), ...(asset.photos_after_works || [])]}
                  photoIndex={0}
                />
              ) : (
                <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center mb-4">
                  <svg className="w-12 h-12 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16"/>
                  </svg>
                </div>
              )}
              
              {/* Photo tabs */}
              {(asset.photos_before_works?.length > 0 || asset.photos_after_works?.length > 0) && (
                <div>
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setActivePhotoTab('before')}
                      className={`flex-1 py-2 text-xs uppercase tracking-wider ${activePhotoTab === 'before' ? 'bg-primary text-white' : 'bg-muted'}`}
                    >
                      {t('asset.before_works')}
                    </button>
                    <button
                      onClick={() => setActivePhotoTab('after')}
                      className={`flex-1 py-2 text-xs uppercase tracking-wider ${activePhotoTab === 'after' ? 'bg-primary text-white' : 'bg-muted'}`}
                    >
                      {t('asset.after_works')}
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {activePhotoTab === 'before' && asset.photos_before_works?.map((photo, idx) => (
                      <ClickablePhoto 
                        key={idx} 
                        url={photo} 
                        alt={`${t('asset.before_works')} ${idx + 1}`} 
                        className="w-full aspect-[4/3] object-cover cursor-pointer"
                        allPhotos={asset.photos_before_works}
                        photoIndex={idx}
                      />
                    ))}
                    {activePhotoTab === 'after' && asset.photos_after_works?.map((photo, idx) => (
                      <ClickablePhoto 
                        key={idx} 
                        url={photo} 
                        alt={`${t('asset.after_works')} ${idx + 1}`} 
                        className="w-full aspect-[4/3] object-cover cursor-pointer"
                        allPhotos={asset.photos_after_works}
                        photoIndex={idx}
                      />
                    ))}
                    {activePhotoTab === 'before' && !asset.photos_before_works?.length && (
                      <p className="col-span-2 text-center text-muted-foreground py-4">{t('asset.no_photo')}</p>
                    )}
                    {activePhotoTab === 'after' && !asset.photos_after_works?.length && (
                      <p className="col-span-2 text-center text-muted-foreground py-4">{t('asset.no_photo')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Info section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 animate-fade-in">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="label-uppercase">{t(`poles.${asset.pole}`, { defaultValue: asset.pole })}</span>
                  <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                    asset.status === 'patrimonial' ? 'bg-secondary text-foreground' 
                    : asset.status === 'marchand_de_bien' ? 'bg-gold/20 text-gold-dark'
                    : 'bg-muted text-muted-foreground'
                  }`}>
                    {t(`status.${asset.status}`, { defaultValue: asset.status })}
                  </span>
                </div>
                <h1 className="font-heading text-4xl text-foreground"><DTInline>{asset.name}</DTInline></h1>
                <p className="text-muted-foreground mt-2"><DTInline>{asset.address}</DTInline></p>
              </div>
              
              {isAdmin && (
                <div className="flex gap-3">
                  <Link
                    to={`/assets/${id}/edit`}
                    className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 uppercase tracking-widest text-sm hover:bg-primary hover:text-white transition-colors"
                    data-testid="edit-asset-btn"
                  >
                    {t('asset.edit')}
                  </Link>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="inline-flex items-center gap-2 border border-destructive text-destructive px-6 py-3 uppercase tracking-widest text-sm hover:bg-destructive/90 hover:text-white transition-colors"
                    data-testid="delete-asset-btn"
                  >
                    {t('asset.delete')}
                  </button>
                </div>
              )}
            </div>
            
            {/* Key metrics */}
            <div className={`grid grid-cols-2 ${isFondsCommerce ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4`}>
              <div className="bg-white border border-border p-4">
                <p className="label-uppercase mb-2">{t('asset.valuation')}</p>
                <p className="font-heading text-2xl tabular-nums">{formatCurrency(asset.current_valuation)}</p>
              </div>
              {!isFondsCommerce && (
                <div className="bg-white border border-border p-4">
                  <p className="label-uppercase mb-2">{isHabitation ? t('asset.seasonal_revenue_short') : t('asset.annual_rent_short')}</p>
                  <p className="font-heading text-2xl tabular-nums">
                    {isHabitation ? formatCurrency(asset.seasonal_revenue || 0) : formatCurrency(asset.annual_rent || 0)}
                  </p>
                </div>
              )}
              <div className="bg-white border border-border p-4">
                <p className="label-uppercase mb-2">{t('asset.ltv')}</p>
                <p className="font-heading text-2xl tabular-nums text-gold-dark">{asset.ltv_ratio?.toFixed(1) || 0}%</p>
              </div>
              <div className="bg-white border border-border p-4">
                <p className="label-uppercase mb-2">{t('asset.ownership')}</p>
                <p className="font-heading text-2xl tabular-nums">{asset.ownership_percentage?.toFixed(0) || 100}%</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-border p-6 space-y-6">
            <h3 className="font-heading text-xl">{t('asset.general_info')}</h3>
            <div className="space-y-4">
              <DetailRow label={t('asset.address')} value={<DTInline>{asset.address}</DTInline>} fullWidth />
              {ownerCompany && (
                <DetailRow
                  label={t('asset.owner_company')}
                  value={
                    <button
                      type="button"
                      onClick={() => navigate(`/orgchart/${ownerCompany.organigramme_id}`)}
                      className="hover:text-gold-dark underline-offset-4 hover:underline transition-colors"
                      data-testid="asset-owner-company-link"
                      title={t('asset.open_orgchart')}>
                      <DTInline>{`${ownerCompany.name}${ownerCompany.title ? ` (${ownerCompany.title})` : ''}`}</DTInline>
                    </button>
                  }
                  fullWidth
                />
              )}
              <DetailRow label={t('asset.surface')} value={`${asset.surface} m²`} />
              {asset.pole === 'hotelerie' && asset.num_rooms && <DetailRow label={t('asset.rooms')} value={asset.num_rooms} />}
              {asset.pole === 'hotelerie' && asset.num_stars && <DetailRow label={t('asset.stars')} value={'★'.repeat(asset.num_stars)} />}
              <DetailRow label={t('asset.acquisition_date')} value={formatDate(asset.acquisition_date)} />
              <DetailRow label={t('asset.acquisition_price')} value={formatCurrency(asset.acquisition_price)} />
              <DetailRow label={t('asset.equity')} value={formatCurrency(asset.equity || 0)} />
              <DetailRow label={t('asset.debt')} value={formatCurrency(asset.debt_amount || 0)} />
              {asset.lender && <DetailRow label={t('asset.lender')} value={<DTInline>{asset.lender}</DTInline>} />}
              {asset.tenant && <DetailRow label={t('asset.tenant')} value={<DTInline>{asset.tenant}</DTInline>} />}
              <DetailRow label={t('asset.ownership_pct')} value={`${asset.ownership_percentage || 100}%`} />
              {asset.description && <DetailRow label={t('asset.description')} value={<DTInline>{asset.description}</DTInline>} fullWidth />}
            </div>
          </div>
          
          <div className="bg-white border border-border p-6 space-y-6">
            <h3 className="font-heading text-xl">{t('asset.performance')}</h3>
            <div className="space-y-4">
              {isSold ? (
                <>
                  <DetailRow label={t('asset.sale_price')} value={formatCurrency(asset.sale_price || 0)} />
                  <DetailRow label={t('asset.sale_date')} value={formatDate(asset.sale_date)} />
                  <DetailRow 
                    label={t('asset.gain_realized')} 
                    value={`${valueChangePercent >= 0 ? '+' : ''}${formatCurrency(valueChange)} (${valueChangePercent.toFixed(1)}%)`} 
                    highlight={valueChangePercent >= 0} 
                  />
                </>
              ) : (
                <>
                  <DetailRow 
                    label={t('asset.gain_latent')} 
                    value={`${valueChangePercent >= 0 ? '+' : ''}${formatCurrency(valueChange)} (${valueChangePercent.toFixed(1)}%)`} 
                    highlight={valueChangePercent >= 0} 
                  />
                  {!isFondsCommerce && <DetailRow label={isHabitation ? t('asset.seasonal_revenue_annual') : t('asset.annual_rent')} value={formatCurrency(annualRent)} />}
                  {!isFondsCommerce && <DetailRow label={t('asset.gross_yield')} value={`${yieldRate.toFixed(2)}%`} />}
                </>
              )}
              <DetailRow label={t('asset.price_per_sqm')} value={formatCurrency(asset.current_valuation / asset.surface)} />
              <DetailRow label={t('asset.ltv_full')} value={`${asset.ltv_ratio?.toFixed(2) || 0}%`} highlight={asset.ltv_ratio < 70} />
            </div>
          </div>
        </div>
        
        {/* Video Section */}
        {asset.video_urls && asset.video_urls.length > 0 && (
          <div className="bg-white border border-border p-6">
            <h3 className="font-heading text-xl mb-6">{t('asset.videos', { count: asset.video_urls.length })}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {asset.video_urls.map((url, idx) => (
                <InlineVideoPlayer 
                  key={idx}
                  url={url}
                  allVideos={asset.video_urls}
                  videoIndex={idx}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-modal">
            <div className="bg-white p-8 max-w-md w-full mx-4">
              <h3 className="font-heading text-2xl mb-4">{t('asset.confirm_delete_title')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('asset.confirm_delete_body', { name: asset.name })}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 border border-border px-6 py-3 uppercase tracking-widest text-sm hover:bg-muted transition-colors"
                  data-testid="cancel-delete-btn"
                >
                  {t('asset.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 bg-destructive text-white px-6 py-3 uppercase tracking-widest text-sm hover:bg-destructive/90 transition-colors"
                  data-testid="confirm-delete-btn"
                >
                  {t('asset.delete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const DetailRow = ({ label, value, highlight, fullWidth }) => (
  <div className={`py-2 border-b border-border last:border-0 ${fullWidth ? '' : 'flex justify-between'}`}>
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-medium ${highlight ? 'text-gold-dark' : ''} ${fullWidth ? 'block mt-1' : ''}`}>{value}</span>
  </div>
);

// Asset Form Page (Create/Edit)
const AssetFormPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const api = useApi();
  const navigate = useNavigate();
  const { isAdmin, token } = useAuth();
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  
  // Pre-fill company from URL param (from orgchart)
  const searchParams = new URLSearchParams(window.location.search);
  const preCompanyId = searchParams.get('company') || '';
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    pole: 'habitation',
    status: 'patrimonial',
    surface: '',
    acquisition_date: '',
    acquisition_price: '',
    current_valuation: '',
    rent_type: 'annuel',
    annual_rent: '',
    seasonal_revenue: '',
    debt_amount: '',
    ownership_percentage: '100',
    sale_price: '',
    sale_date: '',
    description: '',
    main_photo: '',
    photos_before_works: [],
    photos_after_works: [],
    documents: [],
    video_urls: [],
    company_id: preCompanyId,
    num_rooms: '',
    num_stars: '',
    tenant: '',
    equity: '',
    lender: ''
  });
  
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Accès refusé - Mode visiteur");
      navigate("/assets");
      return;
    }
    
    // Fetch companies for the dropdown
    api.get('/orgchart').then(res => setCompanies(res.data)).catch(() => {});
    
    if (isEdit) {
      const fetchAsset = async () => {
        try {
          const response = await api.get(`/assets/${id}`);
          const asset = response.data;
          setFormData({
            name: asset.name || '',
            address: asset.address || '',
            pole: asset.pole || 'habitation',
            status: asset.status || 'patrimonial',
            surface: asset.surface || '',
            acquisition_date: asset.acquisition_date?.split('T')[0] || '',
            acquisition_price: asset.acquisition_price || '',
            current_valuation: asset.current_valuation || '',
            rent_type: asset.rent_type || 'mensuel',
            annual_rent: asset.annual_rent || '',
            seasonal_revenue: asset.seasonal_revenue || '',
            debt_amount: asset.debt_amount || '',
            ownership_percentage: asset.ownership_percentage || '100',
            sale_price: asset.sale_price || '',
            sale_date: asset.sale_date?.split('T')[0] || '',
            description: asset.description || '',
            main_photo: asset.main_photo || '',
            photos_before_works: asset.photos_before_works || [],
            photos_after_works: asset.photos_after_works || [],
            documents: asset.documents || [],
            video_urls: asset.video_urls || [],
            company_id: asset.company_id || '',
            num_rooms: asset.num_rooms || '',
            num_stars: asset.num_stars || '',
            tenant: asset.tenant || '',
            equity: asset.equity || '',
            lender: asset.lender || ''
          });
        } catch (e) {
          toast.error("Actif non trouvé");
          navigate("/assets");
        }
        setLoading(false);
      };
      fetchAsset();
    }
  }, [id, isEdit, isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-set rent type for habitation/promotion
    if (name === 'pole' && (value === 'habitation' || value === 'promotion')) {
      setFormData(prev => ({ ...prev, rent_type: 'saisonnier' }));
    } else if (name === 'pole') {
      setFormData(prev => ({ ...prev, rent_type: 'annuel' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const data = {
        ...formData,
        surface: parseFloat(formData.surface) || 0,
        acquisition_price: parseFloat(formData.acquisition_price) || 0,
        current_valuation: parseFloat(formData.current_valuation) || 0,
        annual_rent: parseFloat(formData.annual_rent) || 0,
        seasonal_revenue: parseFloat(formData.seasonal_revenue) || 0,
        debt_amount: parseFloat(formData.debt_amount) || 0,
        ownership_percentage: parseFloat(formData.ownership_percentage) || 100,
        sale_price: parseFloat(formData.sale_price) || 0,
        num_rooms: formData.num_rooms ? parseInt(formData.num_rooms) : null,
        num_stars: formData.num_stars ? parseInt(formData.num_stars) : null,
        tenant: formData.tenant || '',
        equity: parseFloat(formData.equity) || 0,
        lender: formData.lender || ''
      };
      
      if (isEdit) {
        await api.put(`/assets/${id}`, data);
        toast.success("Actif mis à jour");
      } else {
        await api.post('/assets', data);
        toast.success("Actif créé");
      }
      navigate("/assets");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : "Erreur lors de la sauvegarde");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted w-64" />
          <div className="h-96 bg-muted" />
        </div>
      </Layout>
    );
  }

  const isHabitation = formData.pole === 'habitation' || formData.pole === 'promotion';
  const isFondsCommerce = formData.pole === 'fonds_commerce';
  
  const isSold = formData.status === 'vendu';

  return (
    <Layout>
      <div className="max-w-4xl space-y-8" data-testid="asset-form-page">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>
        
        {/* Header */}
        <div className="animate-fade-in">
          <p className="label-uppercase mb-2">{isEdit ? 'Modifier' : 'Nouveau'} actif</p>
          <h1 className="font-heading text-4xl text-foreground">
            {isEdit ? formData.name : 'Ajouter un actif'}
          </h1>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-border p-8 space-y-8">
          {/* Basic info */}
          <div className="space-y-6">
            <h3 className="font-heading text-xl border-b border-border pb-4">Informations générales</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label-uppercase block mb-2">Nom de l'actif *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: Immeuble Haussmann"
                  required
                  data-testid="input-name"
                />
              </div>
              
              <div>
                <label className="label-uppercase block mb-2">Adresse *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 15 Rue de la Paix, 75002 Paris"
                  required
                  data-testid="input-address"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="label-uppercase block mb-2">Pôle *</label>
                <select
                  name="pole"
                  value={formData.pole}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none"
                  data-testid="select-pole"
                >
                  <option value="habitation">Habitation</option>
                  <option value="hotelerie">Hôtellerie</option>
                  <option value="commercial">Commercial</option>
                  <option value="promotion">Promotion</option>
                  <option value="bureaux">Bureaux</option>
                  <option value="plage_restaurant">Autres</option>
                  <option value="fonds_commerce">Fonds de commerce</option>
                </select>
              </div>
              
              <div>
                <label className="label-uppercase block mb-2">Statut *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none"
                  data-testid="select-status"
                >
                  <option value="patrimonial">Patrimonial (conservé)</option>
                  <option value="vendu">Vendu</option>
                  <option value="marchand_de_bien">Marchand de bien</option>
                </select>
              </div>
              
              <div>
                <label className="label-uppercase block mb-2">Surface (m²) *</label>
                <input
                  type="number"
                  name="surface"
                  value={formData.surface}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 250"
                  required
                  min="0"
                  step="0.01"
                  data-testid="input-surface"
                />
              </div>
            </div>
            
            {/* Hotel-specific fields */}
            {formData.pole === 'hotelerie' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label-uppercase block mb-2">Nombre de chambres</label>
                  <input
                    type="number"
                    name="num_rooms"
                    value={formData.num_rooms}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Ex: 45"
                    min="0"
                    data-testid="input-num-rooms"
                  />
                </div>
                <div>
                  <label className="label-uppercase block mb-2">Nombre d'étoiles</label>
                  <select
                    name="num_stars"
                    value={formData.num_stars}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                    data-testid="input-num-stars"
                  >
                    <option value="">—</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          
          {/* Financial info - Acquisition */}
          <div className="space-y-6">
            <h3 className="font-heading text-xl border-b border-border pb-4">Acquisition</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label-uppercase block mb-2">Date d'acquisition *</label>
                <input
                  type="date"
                  name="acquisition_date"
                  value={formData.acquisition_date}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  required
                  data-testid="input-acquisition-date"
                />
              </div>
              
              <div>
                <label className="label-uppercase block mb-2">Coût de revient (€) *</label>
                <input
                  type="number"
                  name="acquisition_price"
                  value={formData.acquisition_price}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 1500000"
                  required
                  min="0"
                  data-testid="input-acquisition-price"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="label-uppercase block mb-2">Valorisation actuelle (€) *</label>
                <input
                  type="number"
                  name="current_valuation"
                  value={formData.current_valuation}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 1800000"
                  required
                  min="0"
                  data-testid="input-valuation"
                />
              </div>
              
              <div>
                <label className="label-uppercase block mb-2">Dette / Emprunt (€)</label>
                <input
                  type="number"
                  name="debt_amount"
                  value={formData.debt_amount}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 800000"
                  min="0"
                  data-testid="input-debt"
                />
              </div>
              
              <div>
                <label className="label-uppercase block mb-2">% Détention Groupe KEMPF</label>
                <input
                  type="number"
                  name="ownership_percentage"
                  value={formData.ownership_percentage}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 100"
                  min="0"
                  max="100"
                  data-testid="input-ownership"
                />
              </div>
            </div>

            {/* Financement & locataire */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label-uppercase block mb-2">Fonds propres (€)</label>
                <input
                  type="number"
                  name="equity"
                  value={formData.equity}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: 400000"
                  min="0"
                  data-testid="input-equity"
                />
              </div>
              <div>
                <label className="label-uppercase block mb-2">Prêteur</label>
                <input
                  type="text"
                  name="lender"
                  value={formData.lender}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: BNP Paribas"
                  data-testid="input-lender"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-uppercase block mb-2">Locataire</label>
                <input
                  type="text"
                  name="tenant"
                  value={formData.tenant}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Ex: SAS Delivroom"
                  data-testid="input-tenant"
                />
              </div>
            </div>
          </div>
          
          {/* Rent / Revenue */}
          {!isSold && !isFondsCommerce && (
            <div className="space-y-6">
              <h3 className="font-heading text-xl border-b border-border pb-4">
                {isHabitation ? 'Revenus saisonniers' : 'Loyers'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isHabitation ? (
                  <div>
                    <label className="label-uppercase block mb-2">Revenu saisonnier annuel (€)</label>
                    <input
                      type="number"
                      name="seasonal_revenue"
                      value={formData.seasonal_revenue}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                      placeholder="Ex: 45000"
                      min="0"
                      data-testid="input-seasonal"
                    />
                    <p className="text-xs text-muted-foreground mt-2">Revenus totaux sur l'année (location saisonnière)</p>
                  </div>
                ) : (
                  <div>
                    <label className="label-uppercase block mb-2">Loyer annuel (€)</label>
                    <input
                      type="number"
                      name="annual_rent"
                      value={formData.annual_rent}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                      placeholder="Ex: 60000"
                      min="0"
                      data-testid="input-rent"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Sale info (if sold) */}
          {isSold && (
            <div className="space-y-6">
              <h3 className="font-heading text-xl border-b border-border pb-4">Informations de vente</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label-uppercase block mb-2">Prix de vente (€)</label>
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Ex: 2000000"
                    min="0"
                    data-testid="input-sale-price"
                  />
                </div>
                
                <div>
                  <label className="label-uppercase block mb-2">Date de vente</label>
                  <input
                    type="date"
                    name="sale_date"
                    value={formData.sale_date}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-input py-3 focus:border-primary focus:outline-none transition-colors"
                    data-testid="input-sale-date"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Photos */}
          <div className="space-y-6">
            <h3 className="font-heading text-xl border-b border-border pb-4">Photos & Vidéo</h3>
            
            <PhotoUploader
              label="Photo principale"
              value={formData.main_photo}
              onChange={(url) => setFormData(prev => ({ ...prev, main_photo: url }))}
              multiple={false}
              token={token}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PhotoUploader
                label="Photos avant travaux"
                value={formData.photos_before_works}
                onChange={(urls) => setFormData(prev => ({ ...prev, photos_before_works: urls }))}
                multiple={true}
                token={token}
              />
              
              <PhotoUploader
                label="Photos après travaux"
                value={formData.photos_after_works}
                onChange={(urls) => setFormData(prev => ({ ...prev, photos_after_works: urls }))}
                multiple={true}
                token={token}
              />
            </div>
            
            <VideoUploader
              label="Vidéos"
              value={formData.video_urls}
              onChange={(urls) => setFormData(prev => ({ ...prev, video_urls: urls }))}
              token={token}
            />
          </div>
          
          {/* Description */}
          <div className="space-y-6">
            <h3 className="font-heading text-xl border-b border-border pb-4">Description</h3>
            
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full bg-transparent border border-input p-4 focus:border-primary focus:outline-none transition-colors min-h-32 resize-none"
              placeholder="Description de l'actif..."
              data-testid="input-description"
            />
          </div>
          
          {/* Company link */}
          <div>
            <label className="label-uppercase block mb-2">Société (Organigramme)</label>
            <select
              name="company_id"
              value={formData.company_id}
              onChange={handleChange}
              className="w-full bg-transparent border border-input p-3 focus:border-primary focus:outline-none transition-colors"
              data-testid="select-company"
            >
              <option value="">Aucune société</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.title ? ` (${c.title})` : ''}</option>
              ))}
            </select>
          </div>
          
          {/* Submit */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 border border-border px-6 py-4 uppercase tracking-widest text-sm hover:bg-muted transition-colors"
              data-testid="cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-primary text-primary-foreground px-6 py-4 uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="submit-btn"
            >
              {submitting ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer l\'actif')}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

// Main App

// ============== MAP PAGE ==============
// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const POLE_MARKER_COLORS = {
  habitation: '#2E7D32',
  hotelerie: '#C62828',
  commercial: '#1565C0',
  promotion: '#F57F17',
  bureaux: '#5A7B9A',
  plage_restaurant: '#D4825A',
  fonds_commerce: '#8B6F47'
};

const createPoleIcon = (pole) => {
  const color = POLE_MARKER_COLORS[pole] || '#1a1a1a';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="width:28px;height:28px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.35);pointer-events:none;"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16]
  });
};

const createDossierIcon = () => {
  // Distinct visual for "Dossier en cours" — gold square with pulse animation
  return L.divIcon({
    className: 'custom-dossier-marker',
    html: `<div style="position:relative;width:30px;height:30px;pointer-events:none;">
      <div style="position:absolute;inset:0;background:#c5a572;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);transform:rotate(45deg);"></div>
      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:13px;">D</div>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16]
  });
};

const MapPage = () => {
  const { token } = useAuth();
  const isAdmin = useAuth().user?.role === 'admin';
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocodedAssets, setGeocodedAssets] = useState([]);
  const [geocodedDossiers, setGeocodedDossiers] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
  
  const poleLabels = {
    habitation: 'Habitation', hotelerie: 'Hôtellerie', commercial: 'Commercial',
    promotion: 'Promotion', bureaux: 'Bureaux', plage_restaurant: 'Autres',
    fonds_commerce: 'Fonds de commerce'
  };
  
  const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
  
  const fetchAssets = useCallback(async () => {
    try {
      const [aRes, dRes] = await Promise.all([api.get('/assets'), api.get('/dossiers')]);
      setAssets(aRes.data);
      setDossiers(dRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  
  useEffect(() => { fetchAssets(); }, []);
  
  // Geocode assets using their addresses (French Nominatim, with fallback for fuzzy addresses)
  const geocodeAddress = useCallback(async (rawAddress) => {
    const tryQuery = async (q) => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&accept-language=fr&q=${encodeURIComponent(q)}&limit=1`);
        const data = await res.json();
        if (data.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      } catch (e) { /* ignore */ }
      return null;
    };
    const address = (rawAddress || '').trim();
    if (!address) return null;
    // First attempt: full address
    let coords = await tryQuery(address);
    if (coords) return coords;
    // Fallback: last two segments (city + postal code typically)
    const parts = address.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const fallback = parts.slice(-2).join(', ');
      await new Promise(r => setTimeout(r, 1100));
      coords = await tryQuery(fallback);
      if (coords) return coords;
    }
    // Last fallback: keep only the last segment
    if (parts.length >= 1) {
      await new Promise(r => setTimeout(r, 1100));
      coords = await tryQuery(parts[parts.length - 1]);
    }
    return coords;
  }, []);
  
  useEffect(() => {
    if (assets.length === 0) return;
    const doGeocode = async () => {
      setGeocoding(true);
      const results = [];
      for (const asset of assets) {
        if (!asset.address) continue;
        // If coordinates already saved, use them — NEVER re-geocode
        if (asset.latitude != null && asset.longitude != null) {
          results.push({ ...asset, lat: asset.latitude, lng: asset.longitude });
          continue;
        }
        const coords = await geocodeAddress(asset.address);
        if (coords) {
          results.push({ ...asset, lat: coords.lat, lng: coords.lng });
          // Save coords to backend
          try {
            await api.put(`/assets/${asset.id}`, { latitude: coords.lat, longitude: coords.lng });
          } catch (e) { /* ignore */ }
        }
        // Rate limit Nominatim (1 req/sec)
        await new Promise(r => setTimeout(r, 1100));
      }
      setGeocodedAssets(results);
      setGeocoding(false);
    };
    doGeocode();
  }, [assets]);

  // Geocode dossiers as well
  useEffect(() => {
    if (dossiers.length === 0) { setGeocodedDossiers([]); return; }
    const doGeocode = async () => {
      const results = [];
      for (const d of dossiers) {
        if (!d.address) continue;
        if (d.latitude != null && d.longitude != null) {
          results.push({ ...d, lat: d.latitude, lng: d.longitude });
          continue;
        }
        const coords = await geocodeAddress(d.address);
        if (coords) {
          results.push({ ...d, lat: coords.lat, lng: coords.lng });
          try { await api.put(`/dossiers/${d.id}`, { latitude: coords.lat, longitude: coords.lng }); } catch (_) {}
        }
        await new Promise(r => setTimeout(r, 1100));
      }
      setGeocodedDossiers(results);
    };
    doGeocode();
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [dossiers]);

  const handleDossierMarkerDrag = async (dossierId, newLat, newLng) => {
    setGeocodedDossiers(prev => prev.map(d => d.id === dossierId ? { ...d, lat: newLat, lng: newLng } : d));
    try {
      await api.put(`/dossiers/${dossierId}`, { latitude: newLat, longitude: newLng });
      toast.success('Position mise à jour');
    } catch (e) { toast.error("Erreur"); }
  };
  
  const handleMarkerDrag = async (assetId, newLat, newLng) => {
    // Update locally
    setGeocodedAssets(prev => prev.map(a => a.id === assetId ? { ...a, lat: newLat, lng: newLng } : a));
    // Save to backend
    try {
      await api.put(`/assets/${assetId}`, { latitude: newLat, longitude: newLng });
      toast.success('Position mise à jour');
    } catch (e) { toast.error("Erreur"); }
  };
  
  if (loading) return <Layout><div className="flex items-center justify-center h-64"><p>Chargement...</p></div></Layout>;
  
  // Center on Western Europe (France)
  const center = geocodedAssets.length > 0 
    ? [geocodedAssets.reduce((s, a) => s + a.lat, 0) / geocodedAssets.length, geocodedAssets.reduce((s, a) => s + a.lng, 0) / geocodedAssets.length]
    : [46.2, 2.2];
  
  return (
    <Layout>
      <div className="space-y-6" data-testid="map-page">
        <div className="flex items-end justify-between">
          <div>
            <p className="label-uppercase mb-2">Localisation</p>
            <h1 className="font-heading text-4xl">Carte des actifs</h1>
          </div>
          <div className="flex items-center gap-4">
            {geocoding && <p className="text-sm text-muted-foreground animate-pulse">Géolocalisation en cours...</p>}
            <p className="text-sm text-muted-foreground">
              {geocodedAssets.length}/{assets.length} actifs · {geocodedDossiers.length}/{dossiers.length} dossiers
            </p>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4">
          {Object.entries(POLE_MARKER_COLORS).map(([pole, color]) => (
            <div key={pole} className="flex items-center gap-2">
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: color }} />
              <span className="text-xs text-muted-foreground">{poleLabels[pole]}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <div style={{ width: 12, height: 12, background: '#c5a572', transform: 'rotate(45deg)' }} />
            <span className="text-xs text-muted-foreground italic">Dossier en cours</span>
          </div>
          {isAdmin && <span className="text-xs text-muted-foreground ml-4 italic">Glissez les marqueurs pour ajuster la position</span>}
        </div>
        
        {/* Map */}
        <div className="border border-border overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
          <MapContainer center={center} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              subdomains="abcd"
              maxZoom={20}
            />
            {geocodedAssets.map(asset => (
              <DraggableMarker 
                key={asset.id} 
                asset={asset} 
                icon={createPoleIcon(asset.pole)} 
                draggable={isAdmin}
                onDragEnd={handleMarkerDrag}
                navigate={navigate}
                poleLabel={poleLabels[asset.pole]}
                formatCurrency={formatCurrency}
              />
            ))}
            {geocodedDossiers.map(d => (
              <DraggableDossierMarker 
                key={`dossier-${d.id}`} 
                dossier={d}
                draggable={isAdmin}
                onDragEnd={handleDossierMarkerDrag}
                navigate={navigate}
                formatCurrency={formatCurrency}
              />
            ))}
          </MapContainer>
        </div>
      </div>
    </Layout>
  );
};

const DraggableMarker = ({ asset, icon, draggable, onDragEnd, navigate, poleLabel, formatCurrency }) => {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const { lat, lng } = marker.getLatLng();
        onDragEnd(asset.id, lat, lng);
      }
    }
  };
  
  return (
    <Marker 
      position={[asset.lat, asset.lng]} 
      icon={icon} 
      draggable={draggable}
      eventHandlers={eventHandlers}
      ref={markerRef}
    >
      <Popup>
        <div style={{ minWidth: 200 }}>
          <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, fontFamily: 'Playfair Display, serif' }}>{asset.name}</p>
          <p style={{ fontSize: 11, color: '#766a5a', marginBottom: 2 }}>{poleLabel} · {asset.address}</p>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#4a4237' }}>{formatCurrency(asset.current_valuation)}</p>
          <button 
            onClick={() => navigate(`/assets/${asset.id}`)}
            style={{ background: '#4a4237', color: '#f7f5f2', padding: '6px 16px', fontSize: 12, cursor: 'pointer', border: 'none', textTransform: 'uppercase', letterSpacing: '0.05em', width: '100%', fontFamily: 'Manrope, sans-serif' }}
            data-testid={`map-goto-${asset.id}`}>
            Voir la fiche
          </button>
        </div>
      </Popup>
    </Marker>
  );
};

const DOSSIER_STATUS_LABELS = {
  etude: 'En étude',
  offre: 'Offre soumise',
  sous_compromis: 'Sous compromis',
  acte: 'Acté',
};

const DraggableDossierMarker = ({ dossier, draggable, onDragEnd, navigate, formatCurrency }) => {
  const markerRef = useRef(null);
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const { lat, lng } = marker.getLatLng();
        onDragEnd(dossier.id, lat, lng);
      }
    }
  };
  return (
    <Marker
      position={[dossier.lat, dossier.lng]}
      icon={createDossierIcon()}
      draggable={draggable}
      eventHandlers={eventHandlers}
      ref={markerRef}
    >
      <Popup>
        <div style={{ minWidth: 220 }}>
          <p style={{ fontSize: 10, color: '#c5a572', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontWeight: 700 }}>Dossier en cours</p>
          <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, fontFamily: 'Playfair Display, serif' }}>{dossier.name}</p>
          <p style={{ fontSize: 11, color: '#766a5a', marginBottom: 2 }}>{dossier.address}</p>
          <p style={{ fontSize: 11, color: '#766a5a', marginBottom: 6 }}>Statut : <strong>{DOSSIER_STATUS_LABELS[dossier.status] || dossier.status}</strong></p>
          {dossier.current_valuation > 0 && (
            <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#4a4237' }}>{formatCurrency(dossier.current_valuation)}</p>
          )}
          <button
            onClick={() => navigate(`/dossiers/${dossier.id}`)}
            style={{ background: '#c5a572', color: '#fff', padding: '6px 16px', fontSize: 12, cursor: 'pointer', border: 'none', textTransform: 'uppercase', letterSpacing: '0.05em', width: '100%', fontFamily: 'Manrope, sans-serif' }}
            data-testid={`map-goto-dossier-${dossier.id}`}>
            Voir le dossier
          </button>
        </div>
      </Popup>
    </Marker>
  );
};

// ============== ORGCHART PAGE ==============
const OrgChartPage = () => {
  const { token } = useAuth();
  const isAdmin = useAuth().user?.role === 'admin';
  const navigate = useNavigate();

  return (
    <Layout>
      <OrganigrammeList token={token} isAdmin={isAdmin} />
    </Layout>
  );
};

const OrgChartDetailPage = () => {
  const { token } = useAuth();
  const isAdmin = useAuth().user?.role === 'admin';
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <Layout>
      <OrgChartFlow token={token} isAdmin={isAdmin} onNavigate={navigate} organigrammeId={id} />
    </Layout>
  );
};

// ============== ASSOCIATES PAGE ==============
const AssociatesPage = () => {
  const { token } = useAuth();
  const isAdmin = useAuth().user?.role === 'admin';
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editAssoc, setEditAssoc] = useState(null);
  const [formData, setFormData] = useState({ name: '', position: '', photo: '', bio: '', experience: '', cv_file_id: '' });
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [expandedBios, setExpandedBios] = useState({});
  const cvInputRef = useRef(null);
  
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
  
  const fetchAssociates = async () => {
    try {
      const res = await api.get('/associates');
      setAssociates(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  
  useEffect(() => { fetchAssociates(); }, []);
  
  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload-pdf`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setFormData(p => ({ ...p, cv_file_id: res.data.id }));
      toast.success(`CV "${res.data.filename}" uploadé`);
    } catch (e) { toast.error("Erreur upload CV"); }
    setUploading(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editAssoc) {
        await api.put(`/associates/${editAssoc.id}`, formData);
        toast.success('Associé modifié');
      } else {
        await api.post('/associates', formData);
        toast.success('Associé ajouté');
      }
      setShowForm(false);
      setEditAssoc(null);
      setFormData({ name: '', position: '', photo: '', bio: '', experience: '', cv_file_id: '' });
      fetchAssociates();
    } catch (e) { toast.error("Erreur"); }
  };
  
  const handleEdit = (assoc) => {
    setEditAssoc(assoc);
    setFormData({ name: assoc.name, position: assoc.position, photo: assoc.photo || '', bio: assoc.bio || '', experience: assoc.experience || '', cv_file_id: assoc.cv_file_id || '' });
    setShowForm(true);
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/associates/${id}`);
      toast.success('Associé supprimé');
      setConfirmDeleteId(null);
      fetchAssociates();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
      setConfirmDeleteId(null);
    }
  };
  
  const downloadCv = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}`);
      const { data, filename, content_type } = res.data;
      const byteChars = atob(data);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'cv.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur téléchargement CV"); }
  };
  
  const moveAssociate = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= associates.length) return;
    try {
      // Initialize sort_order for all associates based on current display order
      const updates = associates.map((a, i) => {
        const newOrder = i === index ? swapIndex : i === swapIndex ? index : i;
        return api.put(`/associates/${a.id}`, { sort_order: newOrder });
      });
      await Promise.all(updates);
      fetchAssociates();
    } catch (e) { toast.error("Erreur"); }
  };
  
  if (loading) return <Layout><div className="flex items-center justify-center h-64"><p>Chargement...</p></div></Layout>;
  
  return (
    <Layout>
      <div className="space-y-8" data-testid="associates-page">
        <div className="flex items-end justify-between">
          <div>
            <p className="label-uppercase mb-2">Équipe</p>
            <h1 className="font-heading text-4xl">Associés</h1>
          </div>
          {isAdmin && (
            <button onClick={() => { setEditAssoc(null); setFormData({ name: '', position: '', photo: '', bio: '', experience: '', cv_file_id: '' }); setShowForm(true); }}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors"
              data-testid="add-associate-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
              Ajouter
            </button>
          )}
        </div>
        
        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
            <div className="bg-white p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h2 className="font-heading text-2xl mb-6">{editAssoc ? 'Modifier' : 'Ajouter'} un associé</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label-uppercase block mb-1">Nom</label>
                    <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} required
                      className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="assoc-input-name" />
                  </div>
                  <div>
                    <label className="label-uppercase block mb-1">Poste</label>
                    <input value={formData.position} onChange={e => setFormData(p => ({...p, position: e.target.value}))} required
                      className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="assoc-input-position" />
                  </div>
                </div>
                <PhotoUploader label="Photo" value={formData.photo ? [formData.photo] : []}
                  onChange={(url) => setFormData(p => ({...p, photo: url || ''}))} multiple={false} token={token} />
                <div>
                  <label className="label-uppercase block mb-1">Biographie</label>
                  <textarea value={formData.bio} onChange={e => setFormData(p => ({...p, bio: e.target.value}))}
                    className="w-full border border-input px-3 py-2 min-h-20 resize-none focus:border-primary focus:outline-none" data-testid="assoc-input-bio" />
                </div>
                <div>
                  <label className="label-uppercase block mb-1">Expérience</label>
                  <textarea value={formData.experience} onChange={e => setFormData(p => ({...p, experience: e.target.value}))}
                    className="w-full border border-input px-3 py-2 min-h-20 resize-none focus:border-primary focus:outline-none" data-testid="assoc-input-experience" />
                </div>
                <div>
                  <label className="label-uppercase block mb-1">CV (PDF)</label>
                  <input ref={cvInputRef} type="file" accept=".pdf" onChange={handleCvUpload} className="hidden" />
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => cvInputRef.current?.click()} disabled={uploading}
                      className="flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50"
                      data-testid="upload-cv-btn">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M12 18v-6M9 15l3-3 3 3"/>
                      </svg>
                      {uploading ? 'Upload...' : 'Uploader un CV'}
                    </button>
                    {formData.cv_file_id && <span className="text-sm text-gold-dark">CV uploadé</span>}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 uppercase tracking-wider text-sm hover:bg-primary/90" data-testid="assoc-submit-btn">
                    {editAssoc ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Associates Grid */}
        {associates.length === 0 ? (
          <div className="bg-white border border-border p-12 text-center">
            <p className="text-muted-foreground">Aucun associé</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {associates.map((assoc, index) => (
              <div key={assoc.id} className="bg-white border border-border overflow-hidden group" data-testid={`associate-${assoc.id}`}>
                {/* Reorder buttons */}
                {isAdmin && associates.length > 1 && (
                  <div className="flex justify-between items-center px-3 py-1.5 bg-secondary/30 border-b border-border">
                    <button onClick={() => moveAssociate(index, -1)} disabled={index === 0}
                      className={`p-1 rounded transition-colors ${index === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                      title="Déplacer à gauche" data-testid={`move-left-${assoc.id}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <span className="text-xs text-muted-foreground">{index + 1}/{associates.length}</span>
                    <button onClick={() => moveAssociate(index, 1)} disabled={index === associates.length - 1}
                      className={`p-1 rounded transition-colors ${index === associates.length - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                      title="Déplacer à droite" data-testid={`move-right-${assoc.id}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    {assoc.photo ? (
                      <PhotoDisplay url={assoc.photo} className="w-20 h-20 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        <span className="font-heading text-2xl text-foreground">{assoc.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-heading text-xl">{assoc.name}</h3>
                      <p className="text-sm text-gold-dark uppercase tracking-wider">{assoc.position}</p>
                    </div>
                  </div>
                  {(assoc.bio || assoc.experience) && (
                    <>
                      {expandedBios[assoc.id] && (
                        <>
                          {assoc.bio && (
                            <div className="mb-3">
                              <p className="label-uppercase text-xs mb-1">Biographie</p>
                              <p className="text-sm text-muted-foreground whitespace-pre-line">{assoc.bio}</p>
                            </div>
                          )}
                          {assoc.experience && (
                            <div className="mb-3">
                              <p className="label-uppercase text-xs mb-1">Expérience</p>
                              <p className="text-sm text-muted-foreground whitespace-pre-line">{assoc.experience}</p>
                            </div>
                          )}
                        </>
                      )}
                      <button onClick={() => setExpandedBios(p => ({...p, [assoc.id]: !p[assoc.id]}))}
                        className="text-xs text-primary hover:underline mb-2" data-testid={`toggle-bio-${assoc.id}`}>
                        {expandedBios[assoc.id] ? 'Masquer' : 'Voir plus'}
                      </button>
                    </>
                  )}
                  <div className="flex gap-2 pt-3 border-t border-border">
                    {assoc.cv_file_id && (
                      <button onClick={() => downloadCv(assoc.cv_file_id)}
                        className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid={`download-cv-${assoc.id}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M12 11v6M9 14l3 3 3-3"/>
                        </svg>
                        Télécharger CV
                      </button>
                    )}
                    {isAdmin && (
                      <div className="ml-auto flex gap-2">
                        <button onClick={() => handleEdit(assoc)} className="text-sm text-muted-foreground hover:text-foreground">Modifier</button>
                        <button onClick={() => setConfirmDeleteId(assoc.id)} className="text-sm text-destructive hover:text-destructive/80" data-testid={`delete-assoc-${assoc.id}`}>Supprimer</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Delete confirmation modal */}
        {confirmDeleteId && (() => {
          const assocToDelete = associates.find(a => a.id === confirmDeleteId);
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteId(null)}>
              <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="delete-assoc-modal">
                <h2 className="font-heading text-xl mb-3">Supprimer cet associé ?</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Vous allez supprimer <strong>{assocToDelete?.name}</strong>. Cette action est irréversible.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => handleDelete(confirmDeleteId)}
                    className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90 transition-colors"
                    data-testid="confirm-delete-assoc-btn">
                    Supprimer
                  </button>
                  <button onClick={() => setConfirmDeleteId(null)}
                    className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted transition-colors">
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </Layout>
  );
};


// ============== TEAMS PAGE ==============
const TeamsPage = () => {
  const { token } = useAuth();
  const isAdmin = useAuth().user?.role === 'admin';
  const [members, setMembers] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [seniorAdvisors, setSeniorAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formKind, setFormKind] = useState('team'); // 'team' | 'board' | 'senior_advisor'
  const [editMember, setEditMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', position: '', photo: '', bio: '', experience: '', company_ids: [] });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [companies, setCompanies] = useState([]);
  
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
  
  const fetchMembers = async () => {
    try {
      const [teamRes, boardRes, seniorRes, companiesRes] = await Promise.all([
        api.get('/teams?kind=team'),
        api.get('/teams?kind=board'),
        api.get('/teams?kind=senior_advisor'),
        api.get('/orgchart'),
      ]);
      setMembers(teamRes.data);
      setBoardMembers(boardRes.data);
      setSeniorAdvisors(seniorRes.data);
      setCompanies(companiesRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  
  useEffect(() => { fetchMembers(); }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMember) {
        await api.put(`/teams/${editMember.id}`, formData);
        toast.success('Membre modifié');
      } else {
        const list = formKind === 'board' ? boardMembers : formKind === 'senior_advisor' ? seniorAdvisors : members;
        await api.post('/teams', { ...formData, sort_order: list.length, kind: formKind });
        toast.success('Membre ajouté');
      }
      setShowForm(false);
      setEditMember(null);
      setFormData({ name: '', position: '', photo: '', bio: '', experience: '', company_ids: [] });
      fetchMembers();
    } catch (e) { toast.error("Erreur"); }
  };
  
  const handleEdit = (m) => {
    setEditMember(m);
    setFormKind(m.kind || 'team');
    setFormData({ name: m.name, position: m.position, photo: m.photo || '', bio: m.bio || '', experience: m.experience || '', company_ids: m.company_ids || [] });
    setShowForm(true);
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/teams/${id}`);
      toast.success('Membre supprimé');
      setConfirmDeleteId(null);
      fetchMembers();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
      setConfirmDeleteId(null);
    }
  };
  
  const moveItem = async (list, index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= list.length) return;
    try {
      const updates = list.map((m, i) => {
        const newOrder = i === index ? swapIndex : i === swapIndex ? index : i;
        return api.put(`/teams/${m.id}`, { sort_order: newOrder });
      });
      await Promise.all(updates);
      fetchMembers();
    } catch (e) { toast.error("Erreur"); }
  };

  const openAddForm = (kind) => {
    setEditMember(null);
    setFormKind(kind);
    setFormData({ name: '', position: '', photo: '', bio: '', experience: '', company_ids: [] });
    setShowForm(true);
  };

  const renderSection = (title, subtitle, list, kind) => (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-uppercase mb-2">{subtitle}</p>
          <h2 className="font-heading text-3xl">{title}</h2>
        </div>
        {isAdmin && (
          <button onClick={() => openAddForm(kind)}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors"
            data-testid={`add-${kind}-btn`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
            Ajouter
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-border p-10 text-center">
          <p className="text-muted-foreground">Aucun {kind === 'board' ? 'membre du board' : kind === 'senior_advisor' ? 'senior advisor' : "membre dans l'équipe"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((m, index) => (
            <div key={m.id} className="bg-white border border-border overflow-hidden" data-testid={`${kind}-member-${m.id}`}>
              {isAdmin && list.length > 1 && (
                <div className="flex justify-between items-center px-3 py-1.5 bg-secondary/30 border-b border-border">
                  <button onClick={() => moveItem(list, index, -1)} disabled={index === 0}
                    className={`p-1 transition-colors ${index === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  <span className="text-xs text-muted-foreground">{index + 1}/{list.length}</span>
                  <button onClick={() => moveItem(list, index, 1)} disabled={index === list.length - 1}
                    className={`p-1 transition-colors ${index === list.length - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  {m.photo ? (
                    <PhotoDisplay url={m.photo} className="w-20 h-20 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-2xl text-foreground">{m.name?.charAt(0)}</span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-heading text-xl">{m.name}</h3>
                    <p className="text-sm text-gold-dark uppercase tracking-wider">{m.position}</p>
                  </div>
                </div>
                {m.bio && (
                  <div className="mb-3">
                    <p className="label-uppercase text-xs mb-1">Biographie</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{m.bio}</p>
                  </div>
                )}
                {m.experience && (
                  <div className="mb-3">
                    <p className="label-uppercase text-xs mb-1">Expérience</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{m.experience}</p>
                  </div>
                )}
                {(m.company_ids?.length || 0) > 0 && (
                  <div className="mb-3" data-testid={`team-companies-${m.id}`}>
                    <p className="label-uppercase text-xs mb-1">Sociétés rattachées</p>
                    <div className="flex flex-wrap gap-1.5">
                      {m.company_ids.map(cid => {
                        const co = companies.find(c => c.id === cid);
                        return (
                          <span key={cid} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-gold/10 text-gold-dark px-2 py-0.5 rounded-sm border border-gold/30">
                            {co?.name || '?'}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {isAdmin && (
                  <div className="flex gap-2 pt-3 border-t border-border justify-end">
                    <button onClick={() => handleEdit(m)} className="text-sm text-muted-foreground hover:text-foreground">Modifier</button>
                    <button onClick={() => setConfirmDeleteId(m.id)} className="text-sm text-destructive hover:text-destructive/80" data-testid={`delete-${kind}-${m.id}`}>Supprimer</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  
  if (loading) return <Layout><div className="flex items-center justify-center h-64"><p>Chargement...</p></div></Layout>;
  
  return (
    <Layout>
      <div className="space-y-12" data-testid="teams-page">
        {renderSection('Équipes', 'Collaborateurs', members, 'team')}
        {renderSection('Membres du Board', 'Conseil de direction', boardMembers, 'board')}
        {renderSection('Senior Advisor', 'Conseillers seniors', seniorAdvisors, 'senior_advisor')}

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
            <div className="bg-white p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h2 className="font-heading text-2xl mb-1">
                {editMember ? 'Modifier' : 'Ajouter'} un {formKind === 'board' ? 'membre du board' : formKind === 'senior_advisor' ? 'senior advisor' : 'membre'}
              </h2>
              <p className="text-xs text-muted-foreground mb-6 uppercase tracking-wider">
                {formKind === 'board' ? 'Conseil de direction' : formKind === 'senior_advisor' ? 'Conseillers seniors' : 'Équipe'}
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label-uppercase block mb-1">Nom</label>
                    <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} required
                      className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="team-input-name" />
                  </div>
                  <div>
                    <label className="label-uppercase block mb-1">Poste</label>
                    <input value={formData.position} onChange={e => setFormData(p => ({...p, position: e.target.value}))} required
                      className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="team-input-position" />
                  </div>
                </div>
                <PhotoUploader label="Photo" value={formData.photo ? [formData.photo] : []}
                  onChange={(url) => setFormData(p => ({...p, photo: url || ''}))} multiple={false} token={token} />
                <div>
                  <label className="label-uppercase block mb-1">Rattacher à des sociétés</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Le membre apparaîtra automatiquement sous chaque société sélectionnée dans son organigramme.
                    {(formData.company_ids?.length || 0) > 0 && (
                      <span className="ml-1 text-gold-dark font-medium">
                        ({formData.company_ids.length} société{formData.company_ids.length > 1 ? 's' : ''} sélectionnée{formData.company_ids.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-input divide-y divide-border">
                    {companies.length === 0 && (
                      <p className="text-xs text-muted-foreground p-3">Aucune société disponible. Créez d'abord un organigramme.</p>
                    )}
                    {companies.map(c => {
                      const checked = (formData.company_ids || []).includes(c.id);
                      return (
                        <label key={c.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted ${checked ? 'bg-gold/5' : ''}`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => {
                              setFormData(p => {
                                const ids = new Set(p.company_ids || []);
                                if (checked) ids.delete(c.id); else ids.add(c.id);
                                return { ...p, company_ids: Array.from(ids) };
                              });
                            }}
                            className="accent-gold" data-testid={`team-company-checkbox-${c.id}`} />
                          <span className="text-sm">{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="label-uppercase block mb-1">Biographie</label>
                  <textarea value={formData.bio} onChange={e => setFormData(p => ({...p, bio: e.target.value}))}
                    className="w-full border border-input px-3 py-2 min-h-20 resize-none focus:border-primary focus:outline-none" data-testid="team-input-bio" />
                </div>
                <div>
                  <label className="label-uppercase block mb-1">Expérience</label>
                  <textarea value={formData.experience} onChange={e => setFormData(p => ({...p, experience: e.target.value}))}
                    className="w-full border border-input px-3 py-2 min-h-20 resize-none focus:border-primary focus:outline-none" data-testid="team-input-experience" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 uppercase tracking-wider text-sm hover:bg-primary/90" data-testid="team-submit-btn">
                    {editMember ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {confirmDeleteId && (() => {
          const all = [...members, ...boardMembers, ...seniorAdvisors];
          const memberToDelete = all.find(m => m.id === confirmDeleteId);
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteId(null)}>
              <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="delete-team-modal">
                <h2 className="font-heading text-xl mb-3">Supprimer ce membre ?</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Vous allez supprimer <strong>{memberToDelete?.name}</strong>. Cette action est irréversible.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => handleDelete(confirmDeleteId)}
                    className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90 transition-colors"
                    data-testid="confirm-delete-team-btn">
                    Supprimer
                  </button>
                  <button onClick={() => setConfirmDeleteId(null)}
                    className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </Layout>
  );
};


// ===== Legacy / Historique Page =====

const FinancialTable = ({ entries, formatCurrency }) => {
  const hasEnseigne = entries.some(e => e.enseigne);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="financial-table">
        <thead>
          <tr className="border-b border-border text-left bg-muted/30">
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">Ville</th>
            {hasEnseigne && <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">Enseigne</th>}
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Achat</th>
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Prix d'achat</th>
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Vente</th>
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Prix de vente</th>
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Plus-value</th>
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Durée</th>
            <th className="px-5 py-3 text-xs text-muted-foreground uppercase tracking-wider font-medium text-right">Rdt %</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
              <td className="px-5 py-3 font-medium">{e.city}</td>
              {hasEnseigne && <td className="px-5 py-3 text-muted-foreground">{e.enseigne || '—'}</td>}
              <td className="px-5 py-3 text-right tabular-nums">{e.buy_year}</td>
              <td className="px-5 py-3 text-right tabular-nums">{formatCurrency(e.price)}</td>
              <td className="px-5 py-3 text-right tabular-nums">{e.sell_year}</td>
              <td className="px-5 py-3 text-right tabular-nums">{formatCurrency(e.sell_price)}</td>
              <td className={`px-5 py-3 text-right font-medium tabular-nums ${e.gain >= 0 ? 'text-gold-dark' : 'text-destructive'}`}>{formatCurrency(e.gain)}</td>
              <td className="px-5 py-3 text-right text-muted-foreground tabular-nums">{e.hold} ans</td>
              <td className={`px-5 py-3 text-right font-bold tabular-nums ${e.yield_pct >= 0 ? 'text-gold-dark' : 'text-destructive'}`}>{e.yield_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LegacyPage = () => {
  const api = useApi();
  const { isAdmin } = useAuth();
  const [trackRecord, setTrackRecord] = useState(null);
  const [legacyAssets, setLegacyAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', category: '', photos: [] });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const { token } = useAuth();

  const fetchData = async () => {
    try {
      const [tr, la] = await Promise.all([api.get('/track-record'), api.get('/legacy-assets')]);
      setTrackRecord(tr.data);
      setLegacyAssets(la.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const formatCurrency = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await api.put(`/legacy-assets/${editingAsset.id}`, formData);
        toast.success('Fiche modifiée');
      } else {
        await api.post('/legacy-assets', formData);
        toast.success('Fiche créée');
      }
      setShowForm(false);
      setEditingAsset(null);
      setFormData({ name: '', address: '', category: '', photos: [] });
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/legacy-assets/${id}`);
      toast.success('Fiche supprimée');
      setConfirmDeleteId(null);
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({ name: asset.name, address: asset.address || '', category: asset.category || '', photos: asset.photos || [] });
    setShowForm(true);
  };

  const categories = ['Habitation', 'Hôtellerie', 'Commercial', 'Promotion', 'Bureaux', 'Villa'];

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" /></div></Layout>;

  const summary = trackRecord?.summary;
  const tabs = trackRecord?.tabs || [];

  return (
    <Layout>
      <div className="space-y-8 max-w-7xl mx-auto">
        <div>
          <h2 className="font-heading text-3xl text-foreground" data-testid="legacy-page-title">Réalisations de la précédente génération</h2>
        </div>

        {/* Photo familiale + citation */}
        <div className="relative overflow-hidden bg-[#7a7264]" data-testid="legacy-family-photo">
          <div className="flex flex-col md:flex-row items-stretch">
            <div className="w-full md:w-1/2 lg:w-2/5 aspect-[4/3] md:aspect-auto md:min-h-[420px]">
              <img
                src="/famille-kempf.png"
                alt="Famille Kempf"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-full md:w-1/2 lg:w-3/5 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
              <p className="font-heading text-2xl md:text-3xl text-white/95 italic leading-relaxed">
                "Une vision transmise au fil des générations"
              </p>
              <p className="text-white/60 text-xs mt-3 uppercase tracking-widest">Track Record familiale — opérations immobilières réalisées</p>
              <div className="h-px bg-white/20 my-6 md:my-8 w-24" />
              <div className="space-y-4 text-[14px] md:text-[15px] leading-relaxed text-white/85" data-testid="legacy-narrative">
                <p>
                  Au fil des décennies, la famille KEMPF a bâti un patrimoine d'exception fondé sur la qualité, la rigueur et l'esprit entrepreneurial. Le groupe s'est progressivement spécialisé dans l'immobilier commercial de luxe, répondant aux besoins croissants des grandes maisons en quête d'emplacements stratégiques.
                </p>
                <p>
                  Parallèlement, associé à Philippe Brosse, la famille a développé une activité financière aux côtés d'<em className="text-white">Exclusive Partners</em>, société régulée par l'AMF et l'ACPR, qui a conseillé plus de <strong className="text-white">55 milliards d'euros d'actifs en 10 ans</strong>.
                </p>
                <p>
                  En 2025, cette société est cédée à un groupe franco-américain afin de se concentrer pleinement sur son cœur de métier : l'immobilier de luxe sur des sites signatures.
                </p>
                <p>
                  Cette expérience financière demeure un atout majeur, renforçant la capacité du groupe à structurer des opérations complexes avec une vision internationale.
                </p>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white border border-border p-5" data-testid="legacy-kpi-operations">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Opérations</p>
              <p className="font-heading text-2xl mt-1">{summary.total_operations}</p>
            </div>
            <div className="bg-white border border-border p-5" data-testid="legacy-kpi-invested">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Investi</p>
              <p className="font-heading text-2xl mt-1">{formatCurrency(summary.total_invested)}</p>
            </div>
            <div className="bg-white border border-border p-5" data-testid="legacy-kpi-sold">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Cédé</p>
              <p className="font-heading text-2xl mt-1">{formatCurrency(summary.total_sold)}</p>
            </div>
            <div className="bg-white border border-border p-5" data-testid="legacy-kpi-gain">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Plus-value</p>
              <p className="font-heading text-2xl mt-1 text-gold-dark">{formatCurrency(summary.total_gain)}</p>
            </div>
            <div className="bg-white border border-border p-5" data-testid="legacy-kpi-return">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rendement moy.</p>
              <p className="font-heading text-2xl mt-1 text-gold-dark">{summary.avg_return}%</p>
            </div>
          </div>
        )}

        <Tabs defaultValue={tabs[0]?.key || 'appartements'} className="w-full">
          <TabsList className="w-full justify-start bg-transparent border-b border-border rounded-none h-auto p-0 gap-0">
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-3 font-heading text-sm text-muted-foreground data-[state=active]:text-foreground transition-colors"
                data-testid={`tab-${tab.key}`}
              >
                {tab.label}
                <span className="ml-2 text-xs text-muted-foreground font-normal">({tab.entries.length})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map(tab => (
            <TabsContent key={tab.key} value={tab.key} className="mt-6" data-testid={`tab-content-${tab.key}`}>
              {tab.entries.length === 0 ? (
                <div className="text-center py-16 bg-white border border-border">
                  <p className="font-heading text-lg text-muted-foreground">Aucune donnée pour {tab.label}</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">Les données seront ajoutées ultérieurement</p>
                </div>
              ) : (
                <div className="bg-white border border-border">
                  {tab.summary && (
                    <div className="p-5 border-b border-border flex flex-wrap items-center gap-6">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Opérations</p>
                        <p className="font-heading text-lg">{tab.summary.units}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Investi</p>
                        <p className="font-heading text-lg">{formatCurrency(tab.summary.invested)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Cédé</p>
                        <p className="font-heading text-lg">{formatCurrency(tab.summary.sold)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Plus-value</p>
                        <p className="font-heading text-lg text-gold-dark">{formatCurrency(tab.summary.gain)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Rendement moy.</p>
                        <p className="font-heading text-lg text-gold-dark">{tab.summary.avg_return}%</p>
                      </div>
                    </div>
                  )}
                  <FinancialTable entries={tab.entries} formatCurrency={formatCurrency} />
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
};

// Login Page
const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (e) {
      setError(e.response?.data?.detail || t('auth.wrong_credentials'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl tracking-tight text-foreground mb-2">{t('app.brand')}</h1>
          <p className="text-muted-foreground text-sm tracking-wider uppercase">{t('app.subtitle')}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <div className="bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>}
          <div>
            <label className="label-uppercase block mb-2">{t('auth.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-transparent border border-input px-4 py-3 focus:border-primary focus:outline-none transition-colors"
              placeholder="votre@email.com" data-testid="login-email" />
          </div>
          <div>
            <label className="label-uppercase block mb-2">{t('auth.password')}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full bg-transparent border border-input px-4 py-3 focus:border-primary focus:outline-none transition-colors"
              placeholder="••••••••" data-testid="login-password" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-foreground text-white py-3 uppercase tracking-widest text-sm font-heading hover:bg-foreground/90 transition-colors disabled:opacity-50"
            data-testid="login-submit">
            {loading ? `${t('auth.sign_in')}...` : t('auth.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};


function App() {
  return (
    <AuthProvider>
      <DynamicTranslationProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ProtectedRoute><DashboardPremium /></ProtectedRoute>} />
              <Route path="/assets" element={<ProtectedRoute><AssetsListPage /></ProtectedRoute>} />
              <Route path="/map" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
              <Route path="/assets/new" element={<ProtectedRoute><AssetFormPage /></ProtectedRoute>} />
              <Route path="/assets/:id" element={<ProtectedRoute><AssetDetailPage /></ProtectedRoute>} />
              <Route path="/assets/:id/edit" element={<ProtectedRoute><AssetFormPage /></ProtectedRoute>} />
              <Route path="/orgchart" element={<ProtectedRoute><OrgChartPage /></ProtectedRoute>} />
              <Route path="/orgchart/:id" element={<ProtectedRoute><OrgChartDetailPage /></ProtectedRoute>} />
              <Route path="/associates" element={<ProtectedRoute><AssociatesPage /></ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
              <Route path="/dossiers" element={<ProtectedRoute><DossiersListPage /></ProtectedRoute>} />
              <Route path="/dossiers/new" element={<ProtectedRoute><DossierFormPage /></ProtectedRoute>} />
              <Route path="/dossiers/:id" element={<ProtectedRoute><DossierDetailPage /></ProtectedRoute>} />
              <Route path="/dossiers/:id/edit" element={<ProtectedRoute><DossierFormPage /></ProtectedRoute>} />
              <Route path="/legacy" element={<ProtectedRoute><LegacyPage /></ProtectedRoute>} />
              <Route path="/advisors" element={<ProtectedRoute><AdvisorsPage /></ProtectedRoute>} />
              <Route path="/admin/visitor-access" element={<ProtectedRoute><VisitorAccessPage /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <InstallPWABanner />
        </div>
      </DynamicTranslationProvider>
    </AuthProvider>
  );
}

export default App;
